import { useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import type { AsyncFilterOptions } from "@/components/ui/v2/filters/FiltersDrawer";
import { instance } from "@/lib/axios/instance";
import type { FilterOption } from "@/types/FilterOption";

/**
 * One page of options for a single filter dimension. `hasMore` rather than a total: counting a
 * dimension costs the backend as much again as fetching the page, and load-on-scroll only needs to know
 * whether to keep going.
 */
export type FilterOptionPage = {
  items: Required<FilterOption<string>>[];
  hasMore: boolean;
};

/** Kept below the backend's MaxPageSize (200); enough to fill the section without a second fetch. */
const PAGE_SIZE = 50;

/**
 * Load-on-scroll options for one dimension of a report's filter drawer. Shared by the Applies and
 * Candidates drawers — they differ only in the endpoint they page and the unit their counts are in, so
 * this lives here rather than in either module.
 *
 * `enabled` is what keeps the drawer cheap: sections fetch nothing until the user expands them, so
 * opening the drawer costs no requests at all.
 */
export function useFilterOptionsInfinite<TParams extends object>(
  endpoint: string,
  queryKey: readonly unknown[],
  dimension: string,
  params: TParams,
  enabled: boolean,
): AsyncFilterOptions {
  const query = useInfiniteQuery<FilterOptionPage, AxiosError>({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      instance
        .get<FilterOptionPage>(`${endpoint}/${dimension}`, {
          params: {
            ...params,
            page: pageParam as number,
            pageSize: PAGE_SIZE,
          },
        })
        .then((res) => res.data),
    // The backend orders every dimension by its group key, so page N+1 continues where N stopped.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnReconnect: false,
  });

  return {
    options: query.data?.pages.flatMap((page) => page.items) ?? [],
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    isError: query.isError,
    retry: query.refetch,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
  };
}
